import React, { useEffect, useMemo, useRef } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Signal, SignalType } from "@types";
import { getSignalEmailSubject, getSignalFullDate, getSignalTime, getSignalTitle } from "@utils";
import { useAppDispatch } from "@store";
import { getSignals, selectSignal } from "@store/signals";
import { useMixpanel } from "@taskpane/context";
import { Scrollbar } from "@taskpane/components";
import { getSignalIcon } from "./utils";
import { SignalItem } from "./components";
import css from "./SignalsList.module.scss";

export interface SignalsListProps {
  isLoading?: boolean;
  hasNextPage?: boolean;
  list?: Array<Signal | string>;
  headerItemIndices?: number[];
}

const SignalsList: React.FC<SignalsListProps> = ({
  isLoading = false,
  hasNextPage = false,
  list = [],
  headerItemIndices = [],
}) => {
  const dispatch = useAppDispatch();

  const { trackEvent } = useMixpanel();

  const scrollbarRef = useRef<HTMLDivElement>(null);

  const headerItemIndicesSet = useMemo(() => new Set(headerItemIndices), [headerItemIndices]);

  const rowVirtualizer = useVirtualizer({
    overscan: 0,
    count: isLoading ? list.length + 10 : list.length,
    getScrollElement: () => scrollbarRef.current,
    estimateSize: (index) => {
      if (headerItemIndicesSet.has(index) && list.length) {
        return 40;
      }

      const signal = list[index] as Signal;

      return signal?.eventName === SignalType.Welcome ||
        signal?.eventName === SignalType.WelcomeBack ||
        signal?.eventName === SignalType.NewTeamMember
        ? 56
        : 76;
    },
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  const renderSignalsList = () =>
    virtualItems.map(({ index, size, start }) => {
      const item = list[index];
      const isLoaderRow = index > list.length - 1;
      const isHeaderRow = headerItemIndicesSet.has(index);

      const attrs = {
        itemWrapper: {
          className: css.itemWrapper,
          style: {
            position: "absolute" as const,
            top: 0,
            left: 0,
            width: "100%",
            height: `${size}px`,
            transform: `translateY(${start}px)`,
          },
        },
        header: {
          className: css.header,
        },
        signal: {
          icon: getSignalIcon(item as Signal),
          id: (item as Signal)?.eventId,
          subject: getSignalEmailSubject(item as Signal),
          time: getSignalTime(item as Signal),
          timeTooltip: getSignalFullDate((item as Signal)?.eventDate || ""),
          title: getSignalTitle(item as Signal),
          onClick: (id: string) => {
            trackEvent("signalsScreen.openSignalClick");
            dispatch(selectSignal(id));
          },
          ...(typeof item !== "string" &&
            item?.eventName === SignalType.EmailOpened && {
              opensCount: item?.eventMetadata?.accessEvents?.count,
            }),
        },
      };

      if (isLoaderRow) {
        return (
          <div key={index} {...attrs.itemWrapper}>
            <SignalItem isLoading />;
          </div>
        );
      }

      if (isHeaderRow && typeof item === "string") {
        return (
          <div key={index} {...attrs.itemWrapper}>
            <div {...attrs.header}>{item}</div>
          </div>
        );
      }

      return (
        <div key={index} {...attrs.itemWrapper}>
          <SignalItem {...attrs.signal} />
        </div>
      );
    });

  useEffect(() => {
    const [lastItem] = [...rowVirtualizer.getVirtualItems()].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= list.length - 1 && hasNextPage && !isLoading) {
      dispatch(getSignals());
    }
  }, [isLoading, hasNextPage, list, virtualItems]);

  const attrs = {
    scrollbar: {
      scrollableNodeProps: {
        ref: scrollbarRef,
      },
    },
    container: {
      className: css.container,
      style: {
        height: `${rowVirtualizer.getTotalSize()}px`,
      },
    },
  };

  return (
    <Scrollbar {...attrs.scrollbar}>
      <div {...attrs.container}>{renderSignalsList()}</div>
    </Scrollbar>
  );
};

export default SignalsList;
